import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { getNextOccurrenceDate, ruleFromTask } from "../../lib/recurrence";
import { showUndoToast, showTaskLinkToast } from "../../lib/undoStack";
import { Task } from "../../types";

async function patchTaskInDb(
  id: string,
  fields: Record<string, unknown>,
): Promise<boolean> {
  const { error } = await supabase
    .from("tasks")
    .update({ ...fields, updated_at: new Date().toISOString() })
    .eq("id", id);
  return !error;
}

export function useSkipRecurringOccurrence() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return {
    skipRecurringOccurrence: async (id: string): Promise<boolean> => {
      const tasks = queryClient.getQueryData<Task[]>(queryKeys.tasks) ?? [];
      const task = tasks.find((t) => t.id === id);
      if (!task || !task.recurrence_frequency || !task.scheduled_date)
        return false;

      const goToTask = () =>
        navigate({
          to: "/task/$taskId",
          params: { taskId: id },
          search: (prev: Record<string, unknown>) => prev,
        });

      const rule = ruleFromTask(task);
      if (!rule) return false;

      const nextDate = getNextOccurrenceDate(task.scheduled_date, rule);
      const originalDate = task.scheduled_date;

      if (nextDate) {
        // Advance to next occurrence; keep the recurrence rule intact.
        queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
          (prev ?? []).map((t) =>
            t.id === id ? { ...t, scheduled_date: nextDate } : t,
          ),
        );
        const ok = await patchTaskInDb(id, { scheduled_date: nextDate });
        if (!ok) {
          queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
            (prev ?? []).map((t) =>
              t.id === id ? { ...t, scheduled_date: originalDate } : t,
            ),
          );
          Sentry.captureException(
            new Error("skipRecurringOccurrence DB patch failed"),
            { extra: { mutation: "useSkipRecurringOccurrence", taskId: id } },
          );
          showTaskLinkToast({
            prefix: "Failed to skip ",
            label: `"${task.title}"`,
            labelSuffix: ". Please try again.",
            type: "error",
            onLabelClick: goToTask,
          });
          return false;
        }
      } else {
        // Recurrence end date reached — clear the rule and unschedule.
        const clearedFields = {
          scheduled_date: null,
          recurrence_frequency: null,
          recurrence_interval: 1,
          recurrence_day_of_week: null,
          recurrence_end_date: null,
        };
        queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
          (prev ?? []).map((t) =>
            t.id === id ? { ...t, ...clearedFields } : t,
          ),
        );
        const ok = await patchTaskInDb(id, clearedFields);
        if (!ok) {
          queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
            (prev ?? []).map((t) =>
              t.id === id
                ? {
                    ...t,
                    scheduled_date: originalDate,
                    recurrence_frequency: task.recurrence_frequency,
                    recurrence_interval: task.recurrence_interval,
                    recurrence_day_of_week: task.recurrence_day_of_week,
                    recurrence_end_date: task.recurrence_end_date,
                  }
                : t,
            ),
          );
          Sentry.captureException(
            new Error("skipRecurringOccurrence clear failed"),
            {
              extra: {
                mutation: "useSkipRecurringOccurrence/end",
                taskId: id,
              },
            },
          );
          showTaskLinkToast({
            prefix: "Failed to skip ",
            label: `"${task.title}"`,
            labelSuffix: ". Please try again.",
            type: "error",
            onLabelClick: goToTask,
          });
          return false;
        }
      }

      showUndoToast({
        prefix: "Skipped ",
        label: `"${task.title}"`,
        onLabelClick: goToTask,
        onUndo: async () => {
          if (nextDate) {
            const reverted = await patchTaskInDb(id, {
              scheduled_date: originalDate,
            });
            if (reverted) {
              queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
                (prev ?? []).map((t) =>
                  t.id === id ? { ...t, scheduled_date: originalDate } : t,
                ),
              );
            }
          } else {
            // Restore rule and date; recreate the master calendar event.
            const restoredFields = {
              scheduled_date: originalDate,
              recurrence_frequency: task.recurrence_frequency,
              recurrence_interval: task.recurrence_interval,
              recurrence_day_of_week: task.recurrence_day_of_week,
              recurrence_end_date: task.recurrence_end_date,
            };
            const reverted = await patchTaskInDb(id, restoredFields);
            if (reverted) {
              queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
                (prev ?? []).map((t) =>
                  t.id === id ? { ...t, ...restoredFields } : t,
                ),
              );
            }
          }
          queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
        },
      });

      queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
      return true;
    },
  };
}
